import { useState, useEffect } from "react";
import { Modal } from "@/components/ui/Modal";
import { SpinnerIcon } from "@/components/icons";
import { leadService } from "@/domains/Lead/Lead.service";
import type { OutreachTemplate } from "@/domains/Lead/Lead.types";

interface EmailComposeModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipientEmail: string;
  recipientName: string;
  leadId: string;
  initialSubject?: string;
  initialBody?: string;
  onEmailSent?: () => void;
}

export function EmailComposeModal({
  isOpen,
  onClose,
  recipientEmail,
  recipientName,
  leadId,
  initialSubject = "",
  initialBody = "",
  onEmailSent,
}: EmailComposeModalProps) {
  const [template, setTemplate] = useState<OutreachTemplate>("outreach_v1");
  const [subject, setSubject] = useState(initialSubject);
  const [bodyContent, setBodyContent] = useState(initialBody);
  const [headline, setHeadline] = useState("");
  const [ctaUrl, setCtaUrl] = useState("");
  const [ctaText, setCtaText] = useState("");
  const [signatureText, setSignatureText] = useState("");
  const [proofClientName, setProofClientName] = useState("");
  const [proofOutcome, setProofOutcome] = useState("");
  const [proofImageUrl, setProofImageUrl] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  useEffect(() => {
    if (!isOpen) return;
    setTemplate("outreach_v1");
    setSubject(initialSubject);
    setBodyContent(initialBody);
    setHeadline("");
    setCtaUrl("");
    setCtaText("");
    setSignatureText("");
    setProofClientName("");
    setProofOutcome("");
    setProofImageUrl("");
    setError(null);
    setSending(false);
    setShowAdvanced(false);

    // Default to follow-up template when this lead has been emailed before.
    leadService
      .getEmailHistory(leadId)
      .then((sends) => {
        if (sends.length > 0) {
          setTemplate("outreach_v2_followup");
        }
      })
      .catch(() => {
        // Non-fatal — keep the v1 default.
      });
  }, [isOpen, initialSubject, initialBody, leadId]);

  const isFollowup = template === "outreach_v2_followup";

  const handleSend = async () => {
    if (!subject.trim() || !bodyContent.trim()) {
      setError("Subject and body are required");
      return;
    }

    setSending(true);
    setError(null);

    try {
      await leadService.sendEmail(leadId, {
        template,
        subject: subject.trim(),
        body_content: bodyContent.trim(),
        headline: headline.trim() || undefined,
        cta_url: ctaUrl.trim() || undefined,
        cta_text: ctaText.trim() || undefined,
        signature_text: signatureText.trim() || undefined,
        proof_client_name: isFollowup
          ? proofClientName.trim() || undefined
          : undefined,
        proof_outcome: isFollowup
          ? proofOutcome.trim() || undefined
          : undefined,
        proof_image_url: isFollowup
          ? proofImageUrl.trim() || undefined
          : undefined,
      });

      onEmailSent?.();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to send email");
    } finally {
      setSending(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Send Email"
      size="lg"
      footer={
        <>
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800"
            disabled={sending}
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || !subject.trim() || !bodyContent.trim()}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {sending && <SpinnerIcon className="w-4 h-4" />}
            {sending ? "Sending..." : "Send Email"}
          </button>
        </>
      }
    >
      <div className="p-6 space-y-4">
        {/* Recipient info */}
        <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
          <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 text-sm font-medium">
            {recipientName.charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-sm font-medium text-gray-900">{recipientName}</p>
            <p className="text-xs text-gray-500">{recipientEmail}</p>
          </div>
        </div>

        {/* Template picker */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Template
          </label>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setTemplate("outreach_v1")}
              className={`p-3 text-left border rounded-lg text-sm transition ${
                template === "outreach_v1"
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
            >
              <div className="font-medium text-gray-900">First touch</div>
              <div className="text-xs text-gray-500 mt-0.5">
                Win-win pitch with full stats grid.
              </div>
            </button>
            <button
              type="button"
              onClick={() => setTemplate("outreach_v2_followup")}
              className={`p-3 text-left border rounded-lg text-sm transition ${
                template === "outreach_v2_followup"
                  ? "border-blue-500 bg-blue-50"
                  : "border-gray-300 hover:border-gray-400"
              }`}
            >
              <div className="font-medium text-gray-900">Follow-up</div>
              <div className="text-xs text-gray-500 mt-0.5">
                No repeat stats. Lead with a question or an offer.
              </div>
            </button>
          </div>
        </div>

        {/* Subject */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Subject
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder="Email subject line"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
          />
        </div>

        {/* Body */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Body
          </label>
          <textarea
            value={bodyContent}
            onChange={(e) => setBodyContent(e.target.value)}
            placeholder={
              isFollowup
                ? "Lead with a specific question about their business, or a low-stakes offer (portfolio audit, photographer collab, etc.). Keep it short."
                : "Write your email content here. Separate paragraphs with blank lines."
            }
            rows={isFollowup ? 6 : 10}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-y"
          />
          <p className="mt-1 text-xs text-gray-400">
            Plain text. The email template will style it automatically.
          </p>
        </div>

        {/* v2-specific proof block — optional, fill only when you have a real case */}
        {isFollowup && (
          <div className="space-y-3 p-3 border border-blue-200 bg-blue-50 rounded-lg">
            <p className="text-xs text-gray-600">
              Optional case study. Fill in only if you have a real client and a real
              result to point to. Otherwise leave blank — the block won't render.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Named client in their sector
              </label>
              <input
                type="text"
                value={proofClientName}
                onChange={(e) => setProofClientName(e.target.value)}
                placeholder="es. Pizzeria Da Marco"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Concrete outcome
              </label>
              <textarea
                value={proofOutcome}
                onChange={(e) => setProofOutcome(e.target.value)}
                placeholder="es. Da quando gestiamo i loro social, +40% prenotazioni dirette dal profilo Instagram in 3 mesi."
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y"
              />
              <p className="mt-1 text-xs text-gray-500">
                One specific number or result. No vague claims.
              </p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">
                Screenshot URL (optional)
              </label>
              <input
                type="url"
                value={proofImageUrl}
                onChange={(e) => setProofImageUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>
        )}

        {/* Advanced options toggle */}
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="text-sm text-blue-600 hover:text-blue-700"
        >
          {showAdvanced ? "Hide" : "Show"} template options
        </button>

        {showAdvanced && (
          <div className="space-y-3 pt-2 border-t">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Headline (defaults to subject)
              </label>
              <input
                type="text"
                value={headline}
                onChange={(e) => setHeadline(e.target.value)}
                placeholder="Custom headline for the template header"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  CTA Button URL
                </label>
                <input
                  type="url"
                  value={ctaUrl}
                  onChange={(e) => setCtaUrl(e.target.value)}
                  placeholder="https://calendly.com/..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  CTA Button Text
                </label>
                <input
                  type="text"
                  value={ctaText}
                  onChange={(e) => setCtaText(e.target.value)}
                  placeholder="Book a Call"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
                />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Signature note
              </label>
              <input
                type="text"
                value={signatureText}
                onChange={(e) => setSignatureText(e.target.value)}
                placeholder="Looking forward to connecting"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              />
            </div>
          </div>
        )}

        {error && (
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
